import { PanelTop, PanelLeft, PanelRight, Plus, Trash2 } from "lucide-react";
import { bandInfo, setBandCount, setBandGrid, setBandSize, addBand, removeBand } from "@/lib/bands";
import { GLASS_PRESETS } from "@/lib/typology";
import { INFILL_PRESETS } from "@/lib/infill";

const BAND_GLASS = [...new Set(["6mm frosted toughened", "6.38mm laminated frosted", "6.38mm laminated clear", "8.38mm laminated clear", "6mm acid-etched toughened", ...GLASS_PRESETS, ...INFILL_PRESETS])];

const inp = "h-8 w-full px-2 rounded-md bg-[#0B0F19] border border-slate-700 font-mono text-xs text-slate-100 focus:outline-none focus:border-cyan-500";
const META = {
  top: { title: "Overhead panel", empty: "No overhead panel (fanlight / transom light)", icon: PanelTop, size: "Height mm", def: 500, add: "Add overhead", id: "overhead" },
  left: { title: "Left side light", empty: "No left side light", icon: PanelLeft, size: "Width mm", def: 400, add: "Add left", id: "sidelight-left" },
  right: { title: "Right side light", empty: "No right side light", icon: PanelRight, size: "Width mm", def: 400, add: "Add right", id: "sidelight-right" },
};

const Count = ({ label, value, onChange, testid }) => (
  <div>
    <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
    <div className="flex items-center h-8 rounded-md border border-slate-700 bg-[#0B0F19]">
      <button data-testid={`${testid}-dec`} onClick={() => onChange(value - 1)} className="w-8 h-full text-slate-300 hover:text-cyan-300">−</button>
      <span data-testid={testid} className="flex-1 text-center font-mono text-sm">{value}</span>
      <button data-testid={`${testid}-inc`} onClick={() => onChange(value + 1)} className="w-8 h-full text-slate-300 hover:text-cyan-300">+</button>
    </div>
  </div>
);

const Parts = ({ label, parts, onChange, prefix }) => (
  <div>
    <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
    <div className="grid grid-cols-4 gap-1.5">
      {parts.map((v, i) => (
        <input key={`${i}-${parts.join("-")}`} data-testid={`${prefix}-${i}`} type="number" defaultValue={v}
          onBlur={(e) => { const n = Math.max(50, parseInt(e.target.value, 10) || v); if (n !== v) onChange(i, n); }} className={inp} />
      ))}
    </div>
  </div>
);

const rebalance = (parts, i, v, total) => {
  const out = parts.map((c, j) => (j === i ? v : c));
  const k = i === out.length - 1 ? i - 1 : out.length - 1;
  if (k < 0) return null;
  out[k] += total - out.reduce((a, b) => a + b, 0);
  return out[k] > 50 ? out : null;
};

export const BandBox = ({ item, update, side }) => {
  const m = META[side], Icon = m.icon;
  const info = bandInfo(item, side);
  if (!info) {
    return (
      <div className="rounded-md border border-dashed border-slate-700 p-2.5 flex items-center gap-3" data-testid={`${m.id}-box`}>
        <Icon size={15} className="text-slate-500" />
        <span className="text-xs text-slate-400 flex-1">{m.empty}</span>
        <button data-testid={`add-${m.id}-btn`} onClick={() => update(addBand(item, side, m.def))}
          className="h-7 px-2 rounded-md border border-slate-700 text-[11px] text-slate-300 flex items-center gap-1 hover:border-cyan-500 hover:text-cyan-300"><Plus size={12} />{m.add}</button>
      </div>
    );
  }
  const top = side === "top", rev = side === "right";
  const depth = rev ? [...info.depthParts].reverse() : info.depthParts;
  const setLen = (i, v) => { const p = rebalance(info.lengthParts, i, v, info.uB - info.uA); if (p) update(setBandGrid(item, side, p, info.depthParts)); };
  const setDepth = (i, v) => { const p = rebalance(depth, i, v, info.size); if (p) update(setBandGrid(item, side, info.lengthParts, rev ? p.reverse() : p)); };
  const colsCount = top ? info.lengthParts.length : depth.length, rowsCount = top ? depth.length : info.lengthParts.length;
  const setCols = (n) => update(setBandCount(item, side, top ? "length" : "depth", n));
  const setRows = (n) => update(setBandCount(item, side, top ? "depth" : "length", n));
  const colParts = top ? info.lengthParts : depth, rowParts = top ? depth : info.lengthParts;
  const onCol = top ? setLen : setDepth, onRow = top ? setDepth : setLen;
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-3" data-testid={`${m.id}-box`}>
      <div className="flex items-center gap-2">
        <Icon size={15} className="text-amber-300" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-amber-200 flex-1">{m.title} · {colsCount} col × {rowsCount} row</span>
        <button data-testid={`remove-${m.id}-btn`} title={`Remove ${m.title.toLowerCase()}`} onClick={() => update(removeBand(item, side))} className="h-7 w-7 grid place-items-center rounded text-slate-400 hover:text-rose-400"><Trash2 size={13} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">{m.size}</p>
          <input key={info.size} data-testid={`${m.id}-size-input`} type="number" defaultValue={info.size} className={inp}
            onBlur={(e) => { const n = parseInt(e.target.value, 10); if (n && n !== info.size) update(setBandSize(item, side, n)); }} />
        </div>
        <Count label="Vertical cols" value={colsCount} testid={`${m.id}-cols`} onChange={setCols} />
        <Count label="Horizontal rows" value={rowsCount} testid={`${m.id}-rows`} onChange={setRows} />
      </div>
      <div>
        <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">{m.title} glass (default for all its panels)</p>
        <input data-testid={`${m.id}-glass-input`} list={`dl-${m.id}-glass`} value={item.band_glass?.[side] || ""} placeholder={`Same as item: ${item.glass_spec || "—"}`}
          onChange={(e) => update({ ...item, band_glass: { ...(item.band_glass || {}), [side]: e.target.value } })}
          className="h-8 w-full px-2 rounded-md bg-[#0B0F19] border border-slate-700 text-xs text-slate-100 focus:outline-none focus:border-cyan-500" />
        <datalist id={`dl-${m.id}-glass`}>{BAND_GLASS.map((g) => <option key={g} value={g} />)}</datalist>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {["6mm frosted toughened", "6.38mm laminated clear", "6.38mm laminated frosted"].map((g) => (
            <button key={g} data-testid={`${m.id}-glass-chip-${g.split(" ")[0]}-${g.includes("frosted") ? "f" : "c"}`} onClick={() => update({ ...item, band_glass: { ...(item.band_glass || {}), [side]: g } })}
              className={`px-2 py-0.5 rounded-full border text-[10px] transition-colors ${item.band_glass?.[side] === g ? "border-cyan-400 text-cyan-200 bg-cyan-500/10" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>{g.replace(" toughened", "")}</button>
          ))}
        </div>
      </div>
      {colParts.length > 1 || top ? <Parts label="Column widths L→R" parts={colParts} prefix={`${m.id}-col-width`} onChange={onCol} /> : null}
      {rowParts.length > 1 || !top ? <Parts label="Row heights top→bottom" parts={rowParts} prefix={`${m.id}-row-height`} onChange={onRow} /> : null}
    </div>
  );
};

export const BandsSection = ({ item, update }) => (
  <div className="space-y-2" data-testid="bands-section">
    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Overhead & side lights · columns and rows for each</p>
    {["top", "left", "right"].map((s) => <BandBox key={s} side={s} item={item} update={update} />)}
  </div>
);
